import React, { useEffect, useState } from 'react';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import FormField, { PosInput, PosSelect } from '../components/pos/FormField';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
}

export default function AdminPosCatalogPage() {
    const { t } = useTranslation(['common', 'nav', 'messages', 'errors', 'admin']);
    const [units, setUnits] = useState([]);
    const [refs, setRefs] = useState([]);
    const [unitForm, setUnitForm] = useState({ name: '', shortName: '', okeiCode: '' });
    const [refForm, setRefForm] = useState({ category: 'vat_rate', code: '', label: '', numericValue: '', sortOrder: 0 });
    const [error, setError] = useState('');

    async function load() {
        const api = adminApi();
        const [u, r] = await Promise.all([
            api.get('/admin/pos-catalog/measurement-units'),
            api.get('/admin/pos-catalog/reference-values')
        ]);
        setUnits(u.data || []);
        setRefs(r.data || []);
    }

    useEffect(() => { load(); }, []);

    async function addUnit(e) {
        e.preventDefault();
        try {
            await adminApi().post('/admin/pos-catalog/measurement-units', { ...unitForm, isActive: true });
            setUnitForm({ name: '', shortName: '', okeiCode: '' });
            load();
        } catch (err) {
            setError(err.response?.data?.message || t('common:error'));
        }
    }

    async function addRef(e) {
        e.preventDefault();
        try {
            await adminApi().post('/admin/pos-catalog/reference-values', {
                ...refForm,
                numericValue: refForm.numericValue ? parseFloat(refForm.numericValue) : null,
                isActive: true
            });
            setRefForm({ ...refForm, code: '', label: '' });
            load();
        } catch (err) {
            setError(err.response?.data?.message || t('common:error'));
        }
    }

    const catLabel = (cat) => {
        const map = {
            vat_rate: t('admin:posCatalog.catVat'),
            nomenclature_type: t('admin:posCatalog.catNomenclature'),
            payment_subject: t('admin:posCatalog.catPaymentSubject'),
            payment_method54: t('admin:posCatalog.catPaymentMethod'),
            barcode_type: t('admin:posCatalog.catBarcode')
        };
        return map[cat] || cat;
    };

    return (
        <OwnerPageLayout
            title={t('admin:posCatalog.title')}
            subtitle={t('admin:posCatalog.subtitle')}
            className="pos-page pos-ui"
            maxWidth={900}
        >
                {error && <p className="cashier-error">{error}</p>}

                <section className="pos-card" style={{ marginBottom: 24 }}>
                    <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>{t('admin:posCatalog.unitsTitle')}</h2>
                    <form onSubmit={addUnit}>
                        <div className="pos-form-grid">
                            <FormField label={t('admin:posCatalog.name')}>
                                <PosInput value={unitForm.name} onChange={e => setUnitForm({ ...unitForm, name: e.target.value })} required />
                            </FormField>
                            <FormField label={t('admin:posCatalog.shortName')}>
                                <PosInput value={unitForm.shortName} onChange={e => setUnitForm({ ...unitForm, shortName: e.target.value })} required placeholder={t('admin:posCatalog.shortNamePlaceholder')} />
                            </FormField>
                            <FormField label={t('admin:posCatalog.okeiCode')}>
                                <PosInput value={unitForm.okeiCode} onChange={e => setUnitForm({ ...unitForm, okeiCode: e.target.value })} />
                            </FormField>
                        </div>
                        <button type="submit" className="pos-btn pos-btn--primary">{t('admin:posCatalog.addUnit')}</button>
                    </form>
                    <ul style={{ marginTop: 20, paddingLeft: 20, lineHeight: 1.8 }}>
                        {units.map(u => (
                            <li key={u.id}>
                                {t('admin:posCatalog.okeiList', {
                                    short: u.shortName,
                                    name: u.name,
                                    okei: u.okeiCode ? ` (ОКЕИ ${u.okeiCode})` : ''
                                })}{u.isSystem ? ' 🔒' : ''}
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="pos-card">
                    <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>{t('admin:posCatalog.refsTitle')}</h2>
                    <form onSubmit={addRef}>
                        <div className="pos-form-grid">
                            <FormField label={t('admin:posCatalog.category')}>
                                <PosSelect value={refForm.category} onChange={e => setRefForm({ ...refForm, category: e.target.value })}>
                                    <option value="vat_rate">{t('admin:posCatalog.catVat')}</option>
                                    <option value="nomenclature_type">{t('admin:posCatalog.catNomenclature')}</option>
                                    <option value="payment_subject">{t('admin:posCatalog.catPaymentSubject')}</option>
                                    <option value="payment_method54">{t('admin:posCatalog.catPaymentMethod')}</option>
                                    <option value="barcode_type">{t('admin:posCatalog.catBarcode')}</option>
                                </PosSelect>
                            </FormField>
                            <FormField label={t('admin:posCatalog.codeLatin')}>
                                <PosInput value={refForm.code} onChange={e => setRefForm({ ...refForm, code: e.target.value })} required />
                            </FormField>
                            <FormField label={t('admin:posCatalog.labelRu')}>
                                <PosInput value={refForm.label} onChange={e => setRefForm({ ...refForm, label: e.target.value })} required />
                            </FormField>
                            <FormField label={t('admin:posCatalog.numericValue')} hint={t('admin:posCatalog.numericHint')}>
                                <PosInput value={refForm.numericValue} onChange={e => setRefForm({ ...refForm, numericValue: e.target.value })} />
                            </FormField>
                        </div>
                        <button type="submit" className="pos-btn pos-btn--primary">{t('admin:posCatalog.addValue')}</button>
                    </form>
                    {['vat_rate', 'nomenclature_type'].map(cat => (
                        <div key={cat} style={{ marginTop: 20 }}>
                            <strong>{catLabel(cat)}</strong>
                            <ul style={{ paddingLeft: 20 }}>{refs.filter(r => r.category === cat).map(r => (
                                <li key={r.id}>{r.label} ({r.code}) {r.numericValue != null ? `— ${r.numericValue}%` : ''}</li>
                            ))}</ul>
                        </div>
                    ))}
                </section>
        </OwnerPageLayout>
    );
}
